<template>
  <b-row>
    <b-col sm="12">
      <b-card header-tag="header" footer-tag="footer">
        <b-row class="align-items-center">
          <b-col sm="7">
            <ModuleViewHeading
              :module-id="access.module_id"
              :profile-id="access.profile_id"
              :reference="access.view_reference"
            />
          </b-col>
          <b-col sm="5" class="text-right">
            <b-link v-if="table.items.length">
              <export-excel
                class="btn btn-outline-dark btn-sm"
                :data="exportLedgerRows"
                worksheet="Mayor de Cuentas"
                :name="exportLedgerFileName">
                <b-icon icon="download" class="mr-1"></b-icon>
                Exportar Excel
              </export-excel>
            </b-link>
          </b-col>
        </b-row>
      </b-card>

      <b-card header-tag="header" footer-tag="footer">
        <b-row class="align-items-end">
          <b-col md="2">
            <label class="filter-label">Empresa</label>
            <b-form-select size="sm" v-model="filters.business_id" :options="arr.businesses" @change="loadReport()"></b-form-select>
          </b-col>
          <b-col md="1">
            <label class="filter-label">Tipo</label>
            <b-form-select size="sm" v-model="filters.declared" :options="arr.select.declared"></b-form-select>
          </b-col>
          <b-col md="2">
            <label class="filter-label">Desde</label>
            <b-form-input type="date" size="sm" v-model="filters.date_start"></b-form-input>
          </b-col>
          <b-col md="2">
            <label class="filter-label">Hasta</label>
            <b-form-input type="date" size="sm" v-model="filters.date_end"></b-form-input>
          </b-col>
          <b-col md="1">
            <label class="filter-label">Origen</label>
            <b-form-select size="sm" v-model="filters.origin" :options="arr.select.origin"></b-form-select>
          </b-col>
          <b-col md="3">
            <label class="filter-label">Cuentas</label>
            <b-button variant="outline-dark"
                      size="sm"
                      class="accounting-ledger-account-filter"
                      block
                      @click="openAccountsSelector()">
              <b-icon icon="list-check" class="mr-1"></b-icon>
              {{ selectedAccountsSummary }}
            </b-button>
          </b-col>
          <b-col cols="auto" class="d-flex align-items-end">
            <b-button variant="outline-dark" size="sm" class="accounting-ledger-filter-button" @click="loadReport()" v-b-tooltip.hover title="Filtrar">
              <b-icon icon="filter" size="sm" style="font-size:15px;"></b-icon>
            </b-button>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col md="4">
            <b-alert show variant="light" class="mb-2 accounting-ledger-total">
              <b>Debe</b>
              <div>{{ formatCurrency(summary.total_debit) }}</div>
            </b-alert>
          </b-col>
          <b-col md="4">
            <b-alert show variant="light" class="mb-2 accounting-ledger-total">
              <b>Haber</b>
              <div>{{ formatCurrency(summary.total_credit) }}</div>
            </b-alert>
          </b-col>
          <b-col md="4">
            <b-alert show :variant="summary.balance == 0 ? 'success' : 'warning'" class="mb-2 accounting-ledger-total">
              <b>Saldo</b>
              <div>{{ formatCurrency(summary.balance) }}</div>
            </b-alert>
          </b-col>
        </b-row>

        <b-row class="mt-2">
          <b-col>
            <b-table class="mb-0"
                     responsive="sm"
                     head-variant="dark"
                     :hover="true"
                     :small="true"
                     :items="table.items"
                     :fields="table.fields"
                     v-if="table.items.length">
              <template v-slot:cell(account)="data">
                <span :style="{ paddingLeft: (data.item.level * 18) + 'px' }" :class="{ 'font-weight-bold': data.item.is_parent }">
                  <b>{{ data.item.accounting_accounts_code }}</b> - {{ data.item.accounting_accounts_name }}
                </span>
              </template>
              <template v-slot:cell(total_debit)="data">
                <b class="accounting-ledger-amount">{{ formatCurrency(data.item.total_debit) }}</b>
              </template>
              <template v-slot:cell(total_credit)="data">
                <b class="accounting-ledger-amount">{{ formatCurrency(data.item.total_credit) }}</b>
              </template>
              <template v-slot:cell(balance)="data">
                <b-badge :variant="data.item.balance == 0 ? 'success' : 'secondary'" class="accounting-ledger-amount-badge">
                  {{ formatCurrency(data.item.balance) }}
                </b-badge>
              </template>
              <template v-slot:cell(f_action)="data">
                <b-button size="sm" variant="outline-dark" @click="openMovements(data.item)" v-b-tooltip.hover title="Ver movimientos" v-if="data.item.movements.length">
                  <b-icon icon="eye" aria-hidden="true"></b-icon>
                </b-button>
              </template>
            </b-table>
            <b-alert v-else variant="warning" show>No se encontraron registros</b-alert>
          </b-col>
        </b-row>
      </b-card>

      <b-card>
        <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
          <i class="fa fa-angle-double-left"></i>
          Volver
        </b-button>
      </b-card>

      <b-modal v-model="modal.movements.active"
               header-bg-variant="dark"
               header-text-variant="white"
               size="xl"
               :key="modal.movements._id"
               :id="modal.movements._id">
        <div slot="modal-header" minimize>
          {{ modal.movements.title }}
        </div>

        <b-table class="mb-0"
                 responsive="sm"
                 head-variant="dark"
                 :hover="true"
                 :small="true"
                 :items="modal.movements.items"
                 :fields="modal.movements.fields"
                 v-if="modal.movements.items.length">
          <template v-slot:cell(date)="data">
            {{ moment(data.item.date).format('DD/MM/YYYY') }}
          </template>
          <template v-slot:cell(origin)="data">
            <b-badge :variant="data.item.origin == 'manual' ? 'info' : 'secondary'">
              {{ data.item.origin_movement ? data.item.origin_movement.label : data.item.origin }}
            </b-badge>
          </template>
          <template v-slot:cell(debit)="data">
            <b>{{ formatCurrency(data.item.debit) }}</b>
          </template>
          <template v-slot:cell(credit)="data">
            <b>{{ formatCurrency(data.item.credit) }}</b>
          </template>
          <template v-slot:cell(balance)="data">
            <b>{{ formatCurrency(data.item.balance) }}</b>
          </template>
        </b-table>
        <b-alert v-else variant="warning" show>No se encontraron movimientos</b-alert>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" @click="modal.movements.active=false">Cerrar</b-button>
        </div>
      </b-modal>

      <b-modal v-model="modal.accounts.active"
               header-bg-variant="dark"
               header-text-variant="white"
               size="lg"
               :key="modal.accounts._id"
               :id="modal.accounts._id">
        <div slot="modal-header" minimize>
          Seleccionar cuentas
        </div>

        <div class="accounting-ledger-plan-toolbar">
          <b-button size="sm" variant="outline-dark" @click="selectAllAccounts()">Todas</b-button>
          <b-button size="sm" variant="outline-secondary" class="ml-1" @click="clearSelectedAccounts()">Ninguna</b-button>
        </div>
        <div class="accounting-ledger-plan-list">
          <div v-for="account in visibleAccountPlan" :key="'account_plan_' + account.id" class="accounting-ledger-plan-row" :style="{ paddingLeft: (account.level * 16) + 'px' }">
            <b-button size="sm"
                      variant="link"
                      class="accounting-ledger-tree-toggle"
                      @click="toggleExpandedAccount(account)"
                      :disabled="!account.children_ids.length">
              <b-icon :icon="isAccountExpanded(account.id) ? 'chevron-down' : 'chevron-right'" v-if="account.children_ids.length"></b-icon>
            </b-button>
            <b-form-checkbox size="sm"
                             :checked="isAccountSelected(account.id)"
                             @change="toggleAccount(account, $event)">
              <span :class="{ 'font-weight-bold': account.children_ids.length }">
                {{ account.code }} - {{ account.name }}
              </span>
            </b-form-checkbox>
          </div>
        </div>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" @click="modal.accounts.active=false">Cerrar</b-button>
        </div>
      </b-modal>
    </b-col>
  </b-row>
</template>

<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import moment from 'moment'

  export default {
    data: () => {
      return {
        access: {
          module_id: Modules.CONTABILIDAD,
          profile_id: Profiles.PERSONAL,
          view_reference: 'accountingLedger',
          elements: {}
        },
        filters: {
          date_start: '',
          date_end: '',
          origin: '',
          accounting_accounts_ids: [],
          business_id: 0,
          declared: '',
        },
        summary: {
          total_debit: 0,
          total_credit: 0,
          balance: 0,
        },
        table: {
          items: [],
          rawItems: [],
          fields: [
            {key: 'account', label: 'Cuenta', class: 'align-middle'},
            {key: 'total_debit', label: 'Debe', class: 'align-middle text-right'},
            {key: 'total_credit', label: 'Haber', class: 'align-middle text-right'},
            {key: 'balance', label: 'Saldo', class: 'align-middle text-right'},
            {key: 'f_action', label: '', class: 'align-middle text-right'},
          ],
        },
        arr: {
          businesses: [],
          select: {
            origin: [
              { value: '', text: 'Todos' },
              { value: 'automatic', text: 'Auto' },
              { value: 'manual', text: 'Manual' },
            ],
            declared: [
              { value: '', text: 'Todos' },
              { value: '1', text: 'D' },
              { value: '0', text: 'ND' },
            ],
          }
        },
        accountPlanFlat: [],
        selectedAccountIds: [],
        expandedAccountIds: [],
        modal: {
          movements: {
            active: false,
            title: '',
            _id: '',
            items: [],
            fields: [
              {key: 'date', label: 'Fecha', class: 'align-middle'},
              {key: 'reference', label: 'Referencia', class: 'align-middle'},
              {key: 'origin', label: 'Origen', class: 'align-middle text-center'},
              {key: 'debit', label: 'Debe', class: 'align-middle text-right'},
              {key: 'credit', label: 'Haber', class: 'align-middle text-right'},
              {key: 'balance', label: 'Saldo', class: 'align-middle text-right'},
            ],
          },
          accounts: {
            active: false,
            _id: '',
          },
        }
      }
    },
    created () {
      Helper.hasAccessView(this.access)
    },
    mounted() {
      this.filterLoad()
      this.loadBusinesses()
    },
    computed: {
      selectedAccountsSummary() {
        if(!this.accountPlanFlat.length) {
          return 'Cargando plan de cuentas...'
        }

        if(this.selectedAccountIds.length == this.accountPlanFlat.length) {
          return 'Todas las cuentas'
        }

        if(!this.selectedAccountIds.length) {
          return 'Sin cuentas seleccionadas'
        }

        return this.selectedAccountIds.length + ' cuenta(s) seleccionada(s)'
      },
      visibleAccountPlan() {
        return this.accountPlanFlat.filter(account => {
          if(account.level == 0) {
            return true
          }

          let parentId = account.parent_id
          while(parentId) {
            if(this.expandedAccountIds.indexOf(parentId) < 0) {
              return false
            }

            let parent = this.accountPlanFlat.find(item => item.id == parentId)
            parentId = parent ? parent.parent_id : null
          }

          return true
        })
      },
      exportLedgerFileName() {
        let start = this.filters.date_start || 'desde'
        let end = this.filters.date_end || 'hasta'
        return 'mayor-cuentas-' + start + '-' + end + '.xls'
      },
      exportLedgerRows() {
        const fmt = v => parseFloat(v || 0).toFixed(2).replace('.', ',')
        return this.table.items.map(item => {
          return {
            Cuenta: this.getIndentedAccountName(item),
            Debe:  fmt(item.total_debit),
            Haber: fmt(item.total_credit),
            Saldo: fmt(item.balance),
          }
        })
      },
    },
    methods: {
      moment,
      filterLoad() {
        this.filters.date_start = moment().format('YYYY-MM-DD')
        this.filters.date_end = moment().format('YYYY-MM-DD')
      },
      loadBusinesses() {
        serviceAPI.obtenerEmpresas()
        .then(response => {
          const businesses = response.data || []
          this.arr.businesses = businesses.map(b => ({ value: b.id, text: b.name }))
          if(businesses.length) {
            this.filters.business_id = businesses[0].id
          }
          this.loadAccountPlan()
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
          this.loadAccountPlan()
        })
      },
      openAccountsSelector() {
        this.modal.accounts._id = Math.random().toString(36).slice(2, 8)
        this.modal.accounts.active = true
      },
      formatCurrency(value) {
        return Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(value || 0)
      },
      getIndentedAccountName(item) {
        let prefix = ''
        for(let i = 0; i < (item.level || 0); i++) {
          prefix = prefix + '   '
        }

        return prefix + item.accounting_accounts_code + ' - ' + item.accounting_accounts_name
      },
      loadAccountPlan() {
        serviceAPI.filtrarPlanCuenta()
        .then((response) => {
          this.accountPlanFlat = []
          this.flattenAccounts(response.data || [], 0)
          this.selectAllAccounts(false)
          this.loadReport()
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
          this.loadReport()
        })
      },
      flattenAccounts(accounts, level) {
        accounts.forEach(account => {
          let children = account.children_recurrent || account.children || []
          let flatAccount = {
            id: account.id,
            code: account.code,
            name: account.name,
            parent_id: account.parent_id,
            level: level,
            children_ids: [],
          }

          this.accountPlanFlat.push(flatAccount)

          if(children.length) {
            this.flattenAccounts(children, level + 1)
            flatAccount.children_ids = this.collectChildrenIds(children)
          }
        })
      },
      collectChildrenIds(children) {
        let ids = []
        children.forEach(child => {
          ids.push(child.id)
          let childChildren = child.children_recurrent || child.children || []
          if(childChildren.length) {
            ids = ids.concat(this.collectChildrenIds(childChildren))
          }
        })
        return ids
      },
      isAccountSelected(id) {
        return this.selectedAccountIds.indexOf(id) >= 0
      },
      toggleAccount(account, selected) {
        let ids = [account.id].concat(account.children_ids)
        ids.forEach(id => {
          let index = this.selectedAccountIds.indexOf(id)
          if(selected && index < 0) {
            this.selectedAccountIds.push(id)
          }
          if(!selected && index >= 0) {
            this.selectedAccountIds.splice(index, 1)
          }
        })
        this.loadReport()
      },
      selectAllAccounts(load) {
        this.selectedAccountIds = this.accountPlanFlat.map(account => account.id)
        if(load !== false) {
          this.loadReport()
        }
      },
      clearSelectedAccounts() {
        this.selectedAccountIds = []
        this.loadReport()
      },
      toggleExpandedAccount(account) {
        let index = this.expandedAccountIds.indexOf(account.id)
        if(index >= 0) {
          this.expandedAccountIds.splice(index, 1)
        } else {
          this.expandedAccountIds.push(account.id)
        }
      },
      isAccountExpanded(id) {
        return this.expandedAccountIds.indexOf(id) >= 0
      },
      loadReport() {
        let loader = this.$loading.show()
        this.filters.accounting_accounts_ids = this.selectedAccountIds.length ? this.selectedAccountIds : [-1]

        serviceAPI.mayorCuentas(this.filters)
        .then((response) => {
          loader.hide()
          this.summary = response.data.summary || {
            total_debit: 0,
            total_credit: 0,
            balance: 0,
          }
          this.table.rawItems = response.data.accounts || []
          this.table.items = this.buildTreeRows(this.table.rawItems)
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      buildTreeRows(rawItems) {
        let rawByAccount = {}
        rawItems.forEach(item => {
          rawByAccount[item.accounting_accounts_id] = item
        })

        if(!this.accountPlanFlat.length) {
          return rawItems
        }

        let rows = []
        this.accountPlanFlat.forEach(account => {
          if(!this.isAccountSelected(account.id)) {
            return
          }

          let row = rawByAccount[account.id] || {
            accounting_accounts_id: account.id,
            accounting_accounts_code: account.code,
            accounting_accounts_name: account.name,
            total_debit: 0,
            total_credit: 0,
            balance: 0,
            movements: [],
          }

          let totals = this.calculateAccountTotals(account, rawByAccount)
          row.total_debit = totals.total_debit
          row.total_credit = totals.total_credit
          row.balance = totals.balance
          row.level = account.level
          row.is_parent = account.children_ids.length > 0

          if(row.movements.length || row.is_parent) {
            rows.push(row)
          }
        })

        return rows
      },
      calculateAccountTotals(account, rawByAccount) {
        let totalDebit = 0
        let totalCredit = 0
        let ids = [account.id].concat(account.children_ids)

        ids.forEach(id => {
          if(rawByAccount[id]) {
            totalDebit = totalDebit + parseFloat(rawByAccount[id].total_debit || 0)
            totalCredit = totalCredit + parseFloat(rawByAccount[id].total_credit || 0)
          }
        })

        return {
          total_debit: parseFloat(totalDebit.toFixed(2)),
          total_credit: parseFloat(totalCredit.toFixed(2)),
          balance: parseFloat((totalDebit - totalCredit).toFixed(2)),
        }
      },
      openMovements(item) {
        this.modal.movements._id = Math.random().toString(36).slice(2, 8)
        this.modal.movements.title = item.accounting_accounts_code + ' - ' + item.accounting_accounts_name
        this.modal.movements.items = item.movements || []
        this.modal.movements.active = true
      },
    }
  }
</script>

<style scoped>
  .accounting-ledger-selection-summary {
    min-height: 31px;
    padding: 6px 8px;
    border: 1px solid #e4e7ea;
    border-radius: 4px;
    font-size: 13px;
    background: #fff;
  }

  .accounting-ledger-account-filter {
    height: 31px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }

  .accounting-ledger-filter-button {
    width: 31px;
    height: 31px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .accounting-ledger-plan {
    max-height: 520px;
  }

  .accounting-ledger-plan-toolbar {
    padding: 8px;
    border-bottom: 1px solid #e4e7ea;
    background: #f8f9fa;
  }

  .accounting-ledger-plan-list {
    max-height: 470px;
    overflow: auto;
    padding: 8px;
  }

  .accounting-ledger-plan-row {
    min-height: 28px;
    display: flex;
    align-items: center;
  }

  .accounting-ledger-tree-toggle {
    width: 24px;
    height: 24px;
    padding: 0;
    margin-right: 2px;
    color: #343a40;
  }

  .accounting-ledger-total div {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
  }

  .accounting-ledger-amount {
    font-size: 15px;
  }

  .accounting-ledger-amount-badge {
    font-size: 14px;
    padding: 6px 8px;
  }

  .filter-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 2px;
    color: #495057;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
